import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import apiClient from "@shared/services/apiClient";
import logger from "@shared/utils/logger";
import {
  getLiveImportTaskIds,
  reconcilePersistedResumableUploads,
  type ResumableUploadOffer,
} from "../services/reconcileResumableUploads";
import {
  deriveImportTaskProgress,
  loadPersistedImportTasks,
  persistImportTasks,
  type ImportCompletionPayload,
  type ImportWorkflowTask,
  type RecordingStatusPayload,
} from "./eegImportTaskUtils";
import {
  pausePersistedResumableUploadSession,
  removePersistedResumableUploadSession,
} from "@shared/services/resumableEegUploadSessionStore";

type OpenImportModalOptions = {
  patientId?: string;
  studyId?: string;
};

type EegImportTaskContextValue = {
  importTasks: ImportWorkflowTask[];
  isImporting: boolean;
  isRefreshingImportTasks: boolean;
  isReconcilingUploads: boolean;
  resumableUploadOffers: ResumableUploadOffer[];
  showModal: boolean;
  initialPatientId?: string;
  initialStudyId?: string;
  openImportModal: (options?: OpenImportModalOptions) => void;
  closeImportModal: () => void;
  setIsImporting: (value: boolean) => void;
  upsertImportTask: (
    task: Omit<ImportWorkflowTask, "startedAt" | "updatedAt"> & {
      startedAt?: number;
    },
  ) => void;
  updateImportTask: (
    taskId: string,
    updates: Partial<
      Pick<
        ImportWorkflowTask,
        | "filename"
        | "recordingId"
        | "studyId"
        | "patientId"
        | "status"
        | "progress"
        | "stage"
        | "error"
        | "completedAt"
      >
    >,
  ) => void;
  dismissImportTask: (taskId: string) => void;
  dismissResumableOffer: (uploadId: string) => void;
  pauseResumableUpload: (uploadId: string, pausedUntil: number | null) => void;
  unpauseResumableUpload: (uploadId: string) => void;
  refreshImportTaskStatuses: (options?: { silent?: boolean }) => Promise<void>;
  refreshResumableUploadOffers: () => Promise<void>;
  shouldDeferModalReset: () => boolean;
  setPendingImportCompletion: (payload: ImportCompletionPayload | null) => void;
  consumeImportCompletion: (
    handler: (
      payload: ImportCompletionPayload,
    ) => void | Promise<void>,
  ) => void;
  /**
   * Set as soon as upload yields a study/recording id. Keeps the status dock
   * and Studies pending UI up until notifyImportedStudyVisible runs.
   */
  pendingStudyReveal: ImportCompletionPayload | null;
  /** True while post-import UI refresh (navigate/invalidate) is in flight. */
  uiRefreshPending: boolean;
  setUiRefreshPending: (value: boolean) => void;
  /**
   * Patient studies list calls this once the imported study row is committed
   * (useLayoutEffect) so the status dock can dismiss without an empty flash.
   */
  notifyImportedStudyVisible: (studyId: string) => void;
  /**
   * Resolves when notifyImportedStudyVisible(studyId) runs, or after timeoutMs.
   * Used by EegImportShell before clearing uiRefreshPending.
   */
  waitForImportedStudyVisible: (
    studyId: string,
    timeoutMs?: number,
  ) => Promise<boolean>;
  suppressStatusDock: boolean;
  setSuppressStatusDock: (value: boolean) => void;
};

const EegImportTaskContext = createContext<EegImportTaskContextValue | null>(
  null,
);

const STALE_UPLOAD_ERROR =
  "Upload interrupted — session expired or page was refreshed before completion.";

export function useEegImportTasks(): EegImportTaskContextValue {
  const context = useContext(EegImportTaskContext);
  if (!context) {
    throw new Error("useEegImportTasks must be used within EegImportTaskProvider");
  }
  return context;
}

/** Safe for surfaces that may render outside the import provider (tests, detached shells). */
export function useOptionalEegImportTasks(): EegImportTaskContextValue | null {
  return useContext(EegImportTaskContext);
}

export const EegImportTaskProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [importTasks, setImportTasks] = useState<ImportWorkflowTask[]>(() =>
    loadPersistedImportTasks(),
  );
  const [isImporting, setIsImporting] = useState(false);
  const [isRefreshingImportTasks, setIsRefreshingImportTasks] = useState(false);
  const [isReconcilingUploads, setIsReconcilingUploads] = useState(false);
  const [resumableUploadOffers, setResumableUploadOffers] = useState<
    ResumableUploadOffer[]
  >([]);
  const [showModal, setShowModal] = useState(false);
  const [suppressStatusDock, setSuppressStatusDock] = useState(false);
  const [uiRefreshPending, setUiRefreshPending] = useState(false);
  const [pendingStudyReveal, setPendingStudyReveal] =
    useState<ImportCompletionPayload | null>(null);
  const [initialPatientId, setInitialPatientId] = useState<string | undefined>();
  const [initialStudyId, setInitialStudyId] = useState<string | undefined>();

  const importTasksRef = useRef(importTasks);
  const isImportingRef = useRef(isImporting);
  const pendingImportCompletionRef = useRef<ImportCompletionPayload | null>(
    null,
  );
  const hasNotifiedImportCompletionRef = useRef(false);
  const importCompleteHandlerRef = useRef<
    | ((
        payload: ImportCompletionPayload,
      ) => void | Promise<void>)
    | null
  >(null);
  const hasReconciledOnMountRef = useRef(false);
  const studyVisibleWaitersRef = useRef(
    new Map<
      string,
      {
        resolve: (visible: boolean) => void;
        timeoutId: number;
      }
    >(),
  );
  const visibleImportedStudyIdsRef = useRef(new Set<string>());

  const notifyImportedStudyVisible = useCallback((studyId: string) => {
    const key = String(studyId || "").trim();
    if (!key) return;
    visibleImportedStudyIdsRef.current.add(key);
    setPendingStudyReveal((previous) =>
      previous && String(previous.studyId) === key ? null : previous,
    );
    // Study row is committed — dock may dismiss even if Shell is still wrapping up.
    setUiRefreshPending(false);
    const waiter = studyVisibleWaitersRef.current.get(key);
    if (!waiter) return;
    window.clearTimeout(waiter.timeoutId);
    studyVisibleWaitersRef.current.delete(key);
    waiter.resolve(true);
  }, []);

  const waitForImportedStudyVisible = useCallback(
    (studyId: string, timeoutMs = 15000) => {
      const key = String(studyId || "").trim();
      if (!key) return Promise.resolve(false);

      if (visibleImportedStudyIdsRef.current.has(key)) {
        visibleImportedStudyIdsRef.current.delete(key);
        return Promise.resolve(true);
      }

      const existing = studyVisibleWaitersRef.current.get(key);
      if (existing) {
        window.clearTimeout(existing.timeoutId);
        studyVisibleWaitersRef.current.delete(key);
        existing.resolve(false);
      }

      return new Promise<boolean>((resolve) => {
        const timeoutId = window.setTimeout(() => {
          studyVisibleWaitersRef.current.delete(key);
          setPendingStudyReveal((previous) =>
            previous && String(previous.studyId) === key ? null : previous,
          );
          setUiRefreshPending(false);
          resolve(false);
        }, Math.max(500, timeoutMs));
        studyVisibleWaitersRef.current.set(key, { resolve, timeoutId });
      });
    },
    [],
  );

  useEffect(() => {
    importTasksRef.current = importTasks;
    persistImportTasks(importTasks);
  }, [importTasks]);

  useEffect(() => {
    isImportingRef.current = isImporting;
  }, [isImporting]);

  const openImportModal = useCallback((options?: OpenImportModalOptions) => {
    setInitialPatientId(options?.patientId);
    setInitialStudyId(options?.studyId);
    setShowModal(true);
  }, []);

  const closeImportModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const upsertImportTask = useCallback(
    (
      task: Omit<ImportWorkflowTask, "startedAt" | "updatedAt"> & {
        startedAt?: number;
      },
    ) => {
      const now = Date.now();
      setImportTasks((previous) => {
        const index = previous.findIndex((row) => row.id === task.id);
        const nextTask: ImportWorkflowTask = {
          ...task,
          startedAt: task.startedAt ?? now,
          updatedAt: now,
        };
        if (index >= 0) {
          const next = [...previous];
          next[index] = { ...previous[index], ...nextTask, updatedAt: now };
          return next;
        }
        return [...previous, nextTask];
      });
    },
    [],
  );

  const updateImportTask = useCallback(
    (
      taskId: string,
      updates: Partial<
        Pick<
          ImportWorkflowTask,
          | "filename"
          | "recordingId"
          | "studyId"
          | "patientId"
          | "status"
          | "progress"
          | "stage"
          | "error"
          | "completedAt"
        >
      >,
    ) => {
      setImportTasks((previous) =>
        previous.map((task) => {
          if (task.id !== taskId) return task;
          const resolvedUpdates = { ...updates };
          const completedAt =
            resolvedUpdates.completedAt ??
            (resolvedUpdates.status === "complete" ||
            resolvedUpdates.status === "error"
              ? Date.now()
              : task.completedAt);
          return {
            ...task,
            ...resolvedUpdates,
            completedAt,
            updatedAt: Date.now(),
          };
        }),
      );
    },
    [],
  );

  const dismissImportTask = useCallback((taskId: string) => {
    setImportTasks((previous) => previous.filter((task) => task.id !== taskId));
  }, []);

  const dismissResumableOffer = useCallback(
    (uploadId: string) => {
      const offer = resumableUploadOffers.find(
        (row) => row.session.uploadId === uploadId,
      );
      removePersistedResumableUploadSession(uploadId);
      setResumableUploadOffers((previous) =>
        previous.filter((row) => row.session.uploadId !== uploadId),
      );
      const linkedTaskId = offer?.session.importTaskId;
      if (linkedTaskId) {
        updateImportTask(linkedTaskId, {
          status: "error",
          progress: 100,
          stage: "Resume dismissed",
          error: STALE_UPLOAD_ERROR,
        });
      }
    },
    [resumableUploadOffers, updateImportTask],
  );

  const pauseResumableUpload = useCallback(
    (uploadId: string, pausedUntil: number | null) => {
      pausePersistedResumableUploadSession(uploadId, pausedUntil);
      setResumableUploadOffers((previous) =>
        previous.map((offer) =>
          offer.session.uploadId === uploadId
            ? {
                ...offer,
                session: {
                  ...offer.session,
                  pausedUntil,
                  updatedAt: Date.now(),
                },
              }
            : offer,
        ),
      );
      const linkedOffer = resumableUploadOffers.find(
        (offer) => offer.session.uploadId === uploadId,
      );
      if (linkedOffer?.session.importTaskId) {
        updateImportTask(linkedOffer.session.importTaskId, {
          status: "uploading",
          stage:
            pausedUntil === null
              ? "Upload paused until manually resumed"
              : `Upload paused until ${new Date(pausedUntil).toLocaleString()}`,
        });
      }
    },
    [resumableUploadOffers, updateImportTask],
  );

  const unpauseResumableUpload = useCallback(
    (uploadId: string) => {
      pausePersistedResumableUploadSession(uploadId, undefined);
      setResumableUploadOffers((previous) =>
        previous.map((offer) =>
          offer.session.uploadId === uploadId
            ? {
                ...offer,
                session: {
                  ...offer.session,
                  pausedUntil: undefined,
                  updatedAt: Date.now(),
                },
              }
            : offer,
        ),
      );
      const linkedOffer = resumableUploadOffers.find(
        (offer) => offer.session.uploadId === uploadId,
      );
      if (linkedOffer?.session.importTaskId) {
        updateImportTask(linkedOffer.session.importTaskId, {
          status: "uploading",
          stage: "Upload resume requested",
        });
      }
    },
    [resumableUploadOffers, updateImportTask],
  );

  const refreshImportTaskStatuses = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      const activeTasks = importTasksRef.current.filter(
        (task) => task.recordingId && task.status === "processing",
      );
      if (activeTasks.length === 0) return;
      if (!silent) setIsRefreshingImportTasks(true);
      try {
        await Promise.all(
          activeTasks.map(async (task) => {
            try {
              const response = await apiClient.get<RecordingStatusPayload>(
                `/eeg/status/${task.recordingId}`,
              );
              const payload = response.data?.data || response.data;
              const statusUpdate = deriveImportTaskProgress(
                payload?.processing_status || payload?.status,
                payload?.progress,
                task.progress,
              );
              const latestError =
                payload?.error ||
                payload?.logs?.find((log) => log.error_message)
                  ?.error_message ||
                undefined;
              updateImportTask(task.id, {
                ...statusUpdate,
                filename: payload?.filename || task.filename,
                error:
                  statusUpdate.status === "error"
                    ? latestError || "Preprocessing failed."
                    : "",
                ...(statusUpdate.status === "complete" ||
                statusUpdate.status === "error"
                  ? { completedAt: Date.now() }
                  : {}),
              });
            } catch (statusError) {
              logger.warn("Failed to refresh EEG import task status", {
                recordingId: task.recordingId,
                error: statusError,
              });
              const statusCode = (
                statusError as { response?: { status?: number } } | undefined
              )?.response?.status;
              if (statusCode === 404) {
                updateImportTask(task.id, {
                  status: "error",
                  progress: 100,
                  stage: "Recording not found",
                  error:
                    "This import recording is no longer available. Start a new import if needed.",
                  completedAt: Date.now(),
                });
                return;
              }
              if (statusCode === 401 || statusCode === 403) {
                updateImportTask(task.id, {
                  stage: "Sign-in required to refresh status",
                  error:
                    "Your session expired. Refresh the page or sign in again, then retry.",
                });
                return;
              }
              updateImportTask(task.id, {
                stage:
                  statusCode === 503 || statusCode === 504
                    ? "API unavailable — last known preprocessing state"
                    : "Status refresh delayed",
                error:
                  statusCode === 503 || statusCode === 504
                    ? "Unable to confirm progress while the API is unavailable. Retry Refresh shortly."
                    : "Unable to refresh status. Manual refresh can retry.",
              });
            }
          }),
        );
      } finally {
        if (!silent) setIsRefreshingImportTasks(false);
      }
    },
    [updateImportTask],
  );

  const applyReconciliationToImportTasks = useCallback(
    (
      offers: ResumableUploadOffer[],
      options?: { preserveActiveUploads?: boolean },
    ) => {
      const liveImportTaskIds = getLiveImportTaskIds(offers);
      const preserveActive =
        Boolean(options?.preserveActiveUploads) && isImportingRef.current;

      setImportTasks((previous) =>
        previous.map((task) => {
          if (task.status !== "uploading") return task;

          // Do not clobber an in-flight upload worker with "paused" messaging.
          if (preserveActive) {
            return task;
          }

          const linkedOffer = offers.find(
            (offer) => offer.session.importTaskId === task.id,
          );
          if (linkedOffer) {
            const serverPct = Math.round(linkedOffer.progress);
            // Upload maps to 0–70% of the overall import pipeline; keep bar
            // and stage text aligned so "100%" is not shown next to a 70% bar.
            const pipelineProgress = Math.max(
              task.progress,
              Math.min(70, Math.round(linkedOffer.progress * 0.7)),
            );
            const needsFinalize =
              linkedOffer.complete || linkedOffer.progress >= 99.5;
            const stage = needsFinalize
              ? `Upload received (${serverPct}%) — resume to finalize`
              : `Upload paused at ${serverPct}% server · ${pipelineProgress}% overall — resume available`;
            return {
              ...task,
              progress: pipelineProgress,
              stage,
              error: undefined,
              updatedAt: Date.now(),
            };
          }

          if (!liveImportTaskIds.has(task.id)) {
            return {
              ...task,
              status: "error",
              progress: 100,
              stage: "Upload interrupted",
              error: STALE_UPLOAD_ERROR,
              completedAt: Date.now(),
              updatedAt: Date.now(),
            };
          }

          return task;
        }),
      );
    },
    [],
  );

  const refreshResumableUploadOffers = useCallback(async () => {
    setIsReconcilingUploads(true);
    const wasImporting = isImportingRef.current;
    try {
      const result = await reconcilePersistedResumableUploads();
      setResumableUploadOffers(result.offers);
      applyReconciliationToImportTasks(result.offers, {
        preserveActiveUploads: wasImporting,
      });
      if (!wasImporting) {
        setIsImporting(false);
      }

      for (const taskId of result.completedImportTaskIds) {
        updateImportTask(taskId, {
          status: "processing",
          progress: 75,
          stage: "Preprocessing EEG",
        });
      }
    } catch (error) {
      logger.warn("Failed to reconcile resumable EEG upload sessions", { error });
    } finally {
      setIsReconcilingUploads(false);
    }
  }, [applyReconciliationToImportTasks, updateImportTask]);

  useEffect(() => {
    if (hasReconciledOnMountRef.current) return;
    hasReconciledOnMountRef.current = true;
    void refreshResumableUploadOffers();
  }, [refreshResumableUploadOffers]);

  useEffect(() => {
    const hasProcessingTasks = importTasks.some(
      (task) => task.status === "processing" && Boolean(task.recordingId),
    );
    if (!hasProcessingTasks) return undefined;
    void refreshImportTaskStatuses({ silent: true });
    const intervalId = window.setInterval(() => {
      void refreshImportTaskStatuses({ silent: true });
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [importTasks, refreshImportTaskStatuses]);

  useEffect(() => {
    if (hasNotifiedImportCompletionRef.current) return;
    if (importTasks.length === 0) return;
    const hasActiveTasks = importTasks.some(
      (task) => task.status === "uploading" || task.status === "processing",
    );
    const hasFailedTasks = importTasks.some((task) => task.status === "error");
    if (hasActiveTasks || hasFailedTasks) return;

    // Only notify from an explicit completion payload (set when import finishes).
    // Never rebuild from persisted "complete" tasks — that re-navigates to the
    // last imported patient when chrome remounts (e.g. leaving /worklist).
    const payload = pendingImportCompletionRef.current;
    if (!payload) return;

    const handler = importCompleteHandlerRef.current;
    if (!handler) return;

    hasNotifiedImportCompletionRef.current = true;
    setUiRefreshPending(true);
    void (async () => {
      try {
        await handler(payload);
      } finally {
        pendingImportCompletionRef.current = null;
        setUiRefreshPending(false);
      }
    })();
  }, [importTasks]);

  const shouldDeferModalReset = useCallback(
    () =>
      isImportingRef.current ||
      importTasksRef.current.some(
        (task) => task.status === "uploading" || task.status === "processing",
      ) ||
      uiRefreshPending ||
      Boolean(pendingStudyReveal),
    [pendingStudyReveal, uiRefreshPending],
  );

  const setPendingImportCompletion = useCallback(
    (payload: ImportCompletionPayload | null) => {
      pendingImportCompletionRef.current = payload;
      if (payload) {
        // New completion cycle — allow the notify effect / handler flush to run.
        hasNotifiedImportCompletionRef.current = false;
        setPendingStudyReveal(payload);
        setUiRefreshPending(true);
        return;
      }
      // Clearing must not reopen a completed cycle (modal reset used to do this
      // and caused worklist → Patients to jump to the last imported patient).
      setPendingStudyReveal(null);
    },
    [],
  );

  const consumeImportCompletion = useCallback(
    (
      handler: (
        payload: ImportCompletionPayload,
      ) => void | Promise<void>,
    ) => {
      importCompleteHandlerRef.current = handler;
      // Shell mounts late on /worklist (idle chrome). Flush a waiting payload
      // once the handler is registered so we don't drop a real completion.
      const pending = pendingImportCompletionRef.current;
      if (!pending || hasNotifiedImportCompletionRef.current) return;
      hasNotifiedImportCompletionRef.current = true;
      setUiRefreshPending(true);
      void (async () => {
        try {
          await handler(pending);
        } finally {
          pendingImportCompletionRef.current = null;
          setUiRefreshPending(false);
        }
      })();
    },
    [],
  );

  const value = useMemo(
    () => ({
      importTasks,
      isImporting,
      isRefreshingImportTasks,
      isReconcilingUploads,
      resumableUploadOffers,
      showModal,
      initialPatientId,
      initialStudyId,
      openImportModal,
      closeImportModal,
      setIsImporting,
      upsertImportTask,
      updateImportTask,
      dismissImportTask,
      dismissResumableOffer,
      pauseResumableUpload,
      unpauseResumableUpload,
      refreshImportTaskStatuses,
      refreshResumableUploadOffers,
      shouldDeferModalReset,
      setPendingImportCompletion,
      consumeImportCompletion,
      pendingStudyReveal,
      uiRefreshPending,
      setUiRefreshPending,
      notifyImportedStudyVisible,
      waitForImportedStudyVisible,
      suppressStatusDock,
      setSuppressStatusDock,
    }),
    [
      importTasks,
      isImporting,
      isRefreshingImportTasks,
      isReconcilingUploads,
      resumableUploadOffers,
      showModal,
      suppressStatusDock,
      pendingStudyReveal,
      uiRefreshPending,
      initialPatientId,
      initialStudyId,
      openImportModal,
      closeImportModal,
      upsertImportTask,
      updateImportTask,
      dismissImportTask,
      dismissResumableOffer,
      pauseResumableUpload,
      unpauseResumableUpload,
      refreshImportTaskStatuses,
      refreshResumableUploadOffers,
      shouldDeferModalReset,
      setPendingImportCompletion,
      consumeImportCompletion,
      setUiRefreshPending,
      notifyImportedStudyVisible,
      waitForImportedStudyVisible,
      setSuppressStatusDock,
    ],
  );

  return (
    <EegImportTaskContext.Provider value={value}>
      {children}
    </EegImportTaskContext.Provider>
  );
};
